import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { actorFor, humanizeRoleKeys, isMilestoneEvent, recordedByLine, summariseGroup, ukTime } from "./activityFeedModel";

// One originating action (shared meta.action_id) shown as a single collapsible row.
export function ActivityGroupRow({ group, kinds, onInspect }) {
  const [open, setOpen] = useState(false);
  const s = summariseGroup(group.events);
  const kind = kinds[s.head.kind] || kinds.system;
  const Icon = kind.Icon;
  const bodyId = `activity-group-body-${group.id}`;
  const actorText = s.actors.length > 2 ? `${s.actors.slice(0, 2).join(", ")} +${s.actors.length - 2}` : s.actors.join(", ");
  const secondary = [actorText && s.allMilestones ? `Recorded by ${actorText}` : actorText, ...s.parts].filter(Boolean).join(" · ");
  return (
    <li className="border-t border-slate-100 first:border-t-0 bg-white" data-testid={`activity-group-row-${group.id}`} data-group-size={s.count}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={bodyId}
        className="w-full text-left flex items-start gap-3 sm:gap-4 px-2 sm:px-3.5 py-3.5 min-h-[56px] rounded-lg hover:bg-slate-50/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 transition-colors"
        data-testid={`activity-group-toggle-${group.id}`}
      >
        <span className={`shrink-0 w-10 h-10 rounded-xl ring-1 ${kind.ring} ${kind.bg} flex items-center justify-center`} aria-hidden>
          <Icon size={17} className={kind.tint} />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[14.5px] font-semibold text-slate-900 leading-snug break-words" data-testid={`activity-group-title-${group.id}`}>
            {humanizeRoleKeys(s.title)}
          </span>
          <span className="block mt-0.5 text-[13px] text-slate-500" data-testid={`activity-group-count-${group.id}`}>
            {s.count} recorded changes
          </span>
          {secondary && (
            <span className="block mt-0.5 text-[13px] text-slate-500 break-words" data-testid={`activity-group-summary-${group.id}`}>
              {secondary}
            </span>
          )}
        </span>
        <span className="shrink-0 flex items-center gap-2 pt-0.5">
          <span className="w-11 text-right text-[12.5px] text-slate-500 tabular-nums" data-testid={`activity-group-time-${group.id}`}>
            {ukTime(group.atMs)}
          </span>
          <ChevronDown size={16} className={`text-slate-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden />
        </span>
      </button>
      {open && (
        <ul id={bodyId} className="ml-[52px] sm:ml-[70px] mr-1 mb-3 border-l border-slate-200 pl-3" data-testid={`activity-group-body-${group.id}`}>
          {group.events.map((e) => (
            <GroupMember key={e.id} event={e} onInspect={onInspect} />
          ))}
        </ul>
      )}
    </li>
  );
}

function GroupMember({ event, onInspect }) {
  const actor = isMilestoneEvent(event) ? recordedByLine(event) : actorFor(event);
  return (
    <li>
      <button
        type="button"
        onClick={() => onInspect?.(event)}
        className="w-full text-left flex items-start gap-3 py-2 pr-7 sm:pr-[34px] min-h-[44px] rounded-md hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
        data-testid={`activity-group-member-${event.id}`}
      >
        <span className="flex-1 min-w-0">
          <span className={`block text-[13px] leading-snug break-words ${event.superseded ? "text-slate-400 line-through" : "text-slate-800"}`}>
            {humanizeRoleKeys(event.title)}
          </span>
          {actor && <span className="block text-[12px] text-slate-500">{actor}</span>}
        </span>
        <span className={`shrink-0 w-11 text-right text-[12px] text-slate-500 tabular-nums ${isMilestoneEvent(event) ? "invisible" : ""}`}>{ukTime(event.atMs)}</span>
      </button>
    </li>
  );
}
